import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';
import Layout from '../components/Layout';
import { vault } from '../services/api';
import { useToast } from '../context/ToastContext';

interface VaultProfileRow {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  account_count: number;
  updated_at: string | null;
}

const Vault: React.FC = () => {
  const navigate = useNavigate();
  const { toast, confirm } = useToast();
  const [profiles, setProfiles] = useState<VaultProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<VaultProfileRow | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');

  const load = () => {
    setLoading(true);
    vault.getProfiles().then(r => setProfiles(r.data)).catch(() => toast('Unable to load vault profiles', 'error')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setName(''); setDescription(''); setIcon(''); setShowForm(true); };
  const openEdit = (p: VaultProfileRow) => { setEditing(p); setName(p.name); setDescription(p.description || ''); setIcon(p.icon || ''); setShowForm(true); };

  const handleSave = async () => {
    if (!name.trim()) { toast('Profile name is required', 'error'); return; }
    try {
      if (editing) await vault.updateProfile(editing.id, { name, description, icon });
      else await vault.createProfile({ name, description, icon });
      setShowForm(false);
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Unable to save profile', 'error');
    }
  };

  const handleDelete = async (p: VaultProfileRow) => {
    const message = p.account_count > 0
      ? `Delete profile "${p.name}"? This will also permanently delete its ${p.account_count} bank account(s) and all associated vault data.`
      : `Delete profile "${p.name}"?`;
    const ok = await confirm(message);
    if (!ok) return;
    try {
      await vault.deleteProfile(p.id, p.account_count > 0);
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Unable to delete profile', 'error');
    }
  };

  const filtered = profiles.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout title="Banking Vault" subtitle="Your private, encrypted store for banking information">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ position: 'relative', maxWidth: 320, flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search profiles..." style={{ ...inputStyle, paddingLeft: 36 }} />
        </div>
        <button onClick={openCreate} style={primaryButtonStyle}>
          <Plus size={16} /> Add Profile
        </button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', padding: 40, textAlign: 'center' }}>No profiles yet. Add one to get started.</div>
      ) : (
        <div className="grid-3">
          {filtered.map(p => (
            <div key={p.id} onClick={() => navigate(`/vault/profiles/${p.id}`)} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 28 }}>{p.icon || '🗂️'}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={e => { e.stopPropagation(); openEdit(p); }} style={iconButtonStyle}><Pencil size={14} /></button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(p); }} style={iconButtonStyle}><Trash2 size={14} /></button>
                </div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 12 }}>{p.name}</div>
              {p.description && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{p.description}</div>}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>{p.account_count} account{p.account_count === 1 ? '' : 's'}</div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 28, maxWidth: 400, width: '90%' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{editing ? 'Edit Profile' : 'Add Profile'}</div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Profile name (e.g. Dad, Business)" style={inputStyle} />
            <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="Icon (emoji, optional)" style={{ ...inputStyle, marginTop: 10 }} />
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" style={{ ...inputStyle, marginTop: 10, minHeight: 70 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setShowForm(false)} style={cancelButtonStyle}>Cancel</button>
              <button onClick={handleSave} style={primaryButtonStyle}>Save</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14 };
const primaryButtonStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'var(--accent-blue)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const cancelButtonStyle: React.CSSProperties = { padding: '9px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconButtonStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: 6, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' };
const cardStyle: React.CSSProperties = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, cursor: 'pointer', boxShadow: 'var(--shadow-card)' };

export default Vault;
